import loadImage from 'blueimp-load-image';
import Compressor from 'compressorjs';
import { reportError } from 'src/easyclaim.app.libs/reportError';

const SOI_SEGMENT_LENGTH = 2; /** The length of the first segment of a JFIF file. */

/**
 * ISO base media file format "ftyp" box major brands that identify a HEIC/HEIF image.
 * Checking these bytes is enough to detect HEIC without loading the (~2.5 MB) heic-to decoder.
 */
const HEIC_FTYP_BRANDS = new Set([
	'heic',
	'heix',
	'hevc',
	'hevx',
	'heim',
	'heis',
	'hevm',
	'hevs',
	'mif1',
	'msf1',
]);

/**
 * Cheaply detects whether a file is HEIC/HEIF by reading its "ftyp" box, avoiding the need to
 * download the heavy heic-to decoder just to answer this question for every selected image.
 */
async function isLikelyHeic(image: File): Promise<boolean> {
	const header = await image.slice(4, 12).arrayBuffer();

	if (header.byteLength < 8) {
		return false;
	}

	const brand = String.fromCodePoint(...new Uint8Array(header, 4, 4));

	return HEIC_FTYP_BRANDS.has(brand);
}

export interface CompressionOptions {
	maxHeight: number;
	maxWidth: number;
	quality: number;
}

export class ImageCompressionService {
	private readonly compressionOptions: CompressionOptions;

	constructor(compressionOptions: CompressionOptions) {
		this.compressionOptions = compressionOptions;
	}

	/**
	 * Compresses the given image and returns the compressed image as a data URL.
	 *
	 * @param image The image to compress.
	 *
	 * @returns The compressed image as a data URL.
	 */
	public async compressToDataUrl(image: File): Promise<string> {
		let currentFile: Blob;

		if (await isLikelyHeic(image)) {
			const { heicTo } = await import('heic-to/csp');

			currentFile = await heicTo({
				blob: image,
				type: 'image/jpeg',
				quality: 0.8,
			});
		} else {
			currentFile = image;
		}

		const metaData = await this.getMetaData(currentFile);
		const removed = this.removeMetaData(currentFile, metaData);

		const mimeType = removed.type?.startsWith('image/') ? removed.type : 'image/jpeg';
		const blob = removed.slice(0, removed.size, mimeType);

		const compressed = await this.compress(blob);
		const restored = this.restoreMetaData(compressed, metaData);

		return this.blobToDataUrl(restored);
	}

	/**
	 * Returns the metadata of the given image.
	 *
	 * @param image The image to get the metadata from.
	 *
	 * @returns The metadata of the given image.
	 */
	public async getMetaData(image: Blob): Promise<loadImage.MetaData> {
		const options = { disableImageHead: false, maxMetaDataSize: 262144 };

		return new Promise((resolve) => {
			try {
				loadImage.parseMetaData(image, resolve, options);
			} catch (e) {
				reportError(e, { context: 'ImageCompressionService.getMetaData' });
				resolve({});
			}
		});
	}

	/**
	 * Removes the image header from the image.
	 *
	 * @param image The image to remove the header from.
	 * @param metaData The image metadata.
	 *
	 * @returns The image without the given metadata.
	 */
	public removeMetaData(image: Blob, metaData: loadImage.MetaData): Blob {
		if (metaData?.imageHead) {
			const removed = new Blob(
				[
					loadImage.blobSlice.call(image, 0, SOI_SEGMENT_LENGTH),
					loadImage.blobSlice.call(image, metaData.imageHead.byteLength),
				],
				{ type: image.type }
			);

			return removed;
		}

		return image;
	}

	/**
	 * Restores the image header of the given image.
	 *
	 * @param image The original image.
	 * @param metaData The image metadata.
	 *
	 * @returns The image with the given metadata.
	 */
	public restoreMetaData(image: Blob, metaData: loadImage.MetaData): Blob {
		if (metaData?.imageHead) {
			const restored = new Blob(
				[metaData.imageHead as BlobPart, loadImage.blobSlice.call(image, SOI_SEGMENT_LENGTH)],
				{
					type: image.type,
				}
			);

			return restored;
		}

		return image;
	}

	private blobToDataUrl(blob: Blob): Promise<string> {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();

			reader.onloadend = () => resolve(reader.result as string);
			reader.onerror = (ev: ProgressEvent) => reject(new Error(ev.type));
			reader.readAsDataURL(blob);
		});
	}

	private async compress(file: Blob): Promise<Blob> {
		return new Promise(
			(resolve, reject) =>
				new Compressor(file, {
					checkOrientation: false,
					maxHeight: this.compressionOptions.maxHeight,
					maxWidth: this.compressionOptions.maxWidth,
					quality: this.compressionOptions.quality,
					mimeType: 'image/jpeg',
					success: resolve,
					error: reject,
				})
		);
	}
}
